import { makeObservable, observable, action, runInAction } from "mobx";
import { gElementService } from "../services";
import { showToast } from "@/lib/utils/toast";

class GElementStore {
  loadingConsumption = false;
  loadingEmission = false;
  loadingPublish = false;
  error = "";
  consumptionData = null;
  emissionData = null;

  constructor() {
    makeObservable(this, {
      loadingConsumption: observable,
      loadingEmission: observable,
      loadingPublish: observable,
      error: observable,
      consumptionData: observable,
      emissionData: observable,
      resetConsumption: action,
      resetEmission: action,
      fetchConsumptionData: action,
      fetchEmissionData: action,
      publishToSenfi: action,
    });
  }

  resetConsumption = () => {
    this.consumptionData = null;
    this.loadingConsumption = false;
    this.error = "";
  };
  resetEmission = () => {
    this.emissionData = null;
    this.loadingEmission = false;
    this.error = "";
  };
  fetchConsumptionData = async ({
    assetId,
    measurementCode,
    metricCode,
    limit = 1,
    tmStart = "",
    tmEnd = "",
  } = {}) => {
    runInAction(() => {
      this.loadingConsumption = true;
      this.consumptionData = null;
      this.error = "";
    });

    try {
      const payload = {
        asset_id: Number(assetId),
        measurement_code: measurementCode,
        metrics: metricCode,
        limit: Number(limit),
        ...(tmStart && { tm_start: Number(tmStart) }),
        ...(tmEnd && { tm_end: Number(tmEnd) }),
      };
      const res = await gElementService.fetchMeasurementData(payload);
      const records = Array.isArray(res) ? res : (res?.data ?? []);

      if (records.length === 0) {
        throw new Error("No data returned for this period.");
      }
      const limitedRecords =
        records.length > 0 ? records.slice(0, limit) : records;
      runInAction(() => {
        this.consumptionData = limitedRecords;
      });
    } catch (error) {
      runInAction(() => {
        this.consumptionData = error.response?.data;
        showToast.error(error.message || "Unable to load consumption data.");
      });
    } finally {
      runInAction(() => {
        this.loadingConsumption = false;
      });
    }
  };
  fetchEmissionData = async ({
    companyId,
    category,
    customerActivityDataId,
  } = {}) => {
    runInAction(() => {
      this.loadingEmission = true;
      this.emissionData = null;
      this.error = "";
    });

    try {
      const params = new URLSearchParams({
        ...(companyId && { companyId }),
        ...(category && { category }),
        ...(customerActivityDataId && { customerActivityDataId }),
      }).toString();
      const res = await gElementService.fetchEmissionData(
        this.consumptionData,
        params,
      );
      runInAction(() => {
        this.emissionData = res;
      });
    } catch (error) {
      runInAction(() => {
        this.emissionData = null;
        showToast.error(error?.message || "Unable to load emission data.");
      });
    } finally {
      runInAction(() => {
        this.loadingEmission = false;
      });
    }
  };
  publishToSenfi = async () => {
    runInAction(() => {
      this.loadingPublish = true;
      this.error = "";
    });

    try {
      const res = await gElementService.publishToSenfi(this.emissionData);
      runInAction(() => {
        if (res?.success) {
          showToast.success("Published to Senfi successfully.");
          this.resetEmission();
        }
      });
    } catch (error) {
      runInAction(() => {
        showToast.error(error?.message || "Unable to publish to Senfi.");
      });
    } finally {
      runInAction(() => {
        this.loadingPublish = false;
      });
    }
  };
}

export const gElementStore = new GElementStore();
